import { textoContratoAHtml } from './contratoTextoFormato';
import { obtenerParrafosArticulo } from './articuloPlantillaContrato';
import { fechaContratoEnLetras } from './fechaContratoEnLetras';
import { montoContratoEnLetras } from './montoEnLetrasSoles';
import { resolverStackFuenteContrato, resolverCssFontFamilyContrato } from './contratoFuentes';

function escapeHtml(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Sustituye {{clave}} por valores del mapa; deja el token si no hay valor. */
export function resolverVariablesPlantilla(texto, mapa = {}) {
  if (!texto) return '';

  return String(texto).replace(/\{\{\s*([^}\s]+(?:\.[^}\s]+)*)\s*\}\}/g, (match, key) => {
    const clave = key.trim();
    if (Object.prototype.hasOwnProperty.call(mapa, clave)) {
      const valor = mapa[clave];
      return valor == null || valor === '' ? `[${clave}]` : String(valor);
    }
    return match;
  });
}

const DATOS_EJEMPLO = {
  'empresa.nombre_empresa': 'Empresa Ejemplo S.A.C.',
  'empresa.ruc': '20123456789',
  'empresa.representante_legal': 'Juan Pérez García',
  'empresa.direccion': 'Av. Principal 123, Lima',
  'empresa.dni_representante': '12345678',
  'empleado.nombre_completo': 'María López Torres',
  'empleado.tipo_documento': 'DNI',
  'empleado.numero_documento': '87654321',
  'empleado.fecha_nacimiento': '15/03/1990',
  'empleado.direccion': 'Jr. Los Olivos 456',
  'empleado.distrito': 'Miraflores',
  'empleado.provincia': 'Lima',
  'empleado.departamento': 'Lima',
  'empleado.cargo': 'Operario de mina',
  'empleado.funciones_cargo':
    'a) Ejecutar las labores asignadas en el área de trabajo conforme a las normas de seguridad vigentes.\n' +
    'b) Operar y mantener en buen estado el equipo y herramientas a su cargo.\n' +
    'c) Reportar oportunamente incidentes, accidentes o anomalías detectadas durante la jornada.\n' +
    'd) Cumplir con las políticas internas, reglamento interno y disposiciones del empleador.',
  'empleado.categoria': 'Obrero',
  'empleado.proyecto': 'Proyecto Norte',
  'empleado.areas_de_trabajo': 'Mina subterránea',
  'empleado.tipo_contratacion': 'Planilla',
  'empleado.sueldo_basico': '3,500.00',
  'empleado.sueldo_basico_escrito': montoContratoEnLetras(3500),
  'empleado.banco': 'BCP',
  'empleado.cuenta_bancaria': '191-12345678901',
  'empleado.cci': '00219100123456789012',
  'historial.fecha_ingreso': '01/01/2026',
  'historial.fecha_cese': '—',
  'contrato.fecha_firma': new Date().toLocaleDateString('es-PE'),
  'contrato.fecha_firma_escrito': fechaContratoEnLetras(new Date()),
  'contrato.lugar_firma': 'Lima',
  'contrato.fecha_inicio': '01/01/2026',
  'contrato.fecha_inicio_escrito': fechaContratoEnLetras('2026-01-01'),
  'contrato.plazo_meses': '12',
  'contrato.fecha_fin': '31/12/2026',
};

export function construirMapaVistaPrevia(empresa = null) {
  const mapa = { ...DATOS_EJEMPLO };

  if (empresa) {
    const nombre = empresa.nombre_empresa || empresa.name || empresa.nombre;
    if (nombre) mapa['empresa.nombre_empresa'] = nombre;
    if (empresa.ruc) mapa['empresa.ruc'] = empresa.ruc;
    if (empresa.representante_legal) mapa['empresa.representante_legal'] = empresa.representante_legal;
    if (empresa.direccion) mapa['empresa.direccion'] = empresa.direccion;
    if (empresa.dni_representante) mapa['empresa.dni_representante'] = empresa.dni_representante;
  }

  return mapa;
}

function tituloArticuloVisible(articulo) {
  return (articulo?.titulo || '').trim();
}

/** Texto resuelto → uno o más <p> (salto de página por párrafo, no por artículo). */
function textoContratoParrafosHtml(texto, mapa, estiloParrafo) {
  const resuelto = resolverVariablesPlantilla(texto || '', mapa).trim();
  if (!resuelto) return '';

  const parrafos = resuelto.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const lista = parrafos.length > 0 ? parrafos : [resuelto];

  return lista
    .map(
      (parrafo) =>
        `<p data-contrato-parrafo="1" style="${estiloParrafo}">${textoContratoAHtml(parrafo)}</p>`
    )
    .join('');
}

const TAMANOS_FUENTE_CONTRATO = {
  parrafo: 13,
  empresaTitulo: 19,
  empresaDetalle: 12,
  tipoContrato: 12,
  tituloDocumento: 16,
  tituloArticulo: 14,
  pieFecha: 12,
  firmaHint: 11,
  firmaRol: 11,
  firmaNombre: 12,
  firmaDetalle: 11,
};

const ESTILO_PARRAFO =
  `margin: 0 0 10px; font-size: ${TAMANOS_FUENTE_CONTRATO.parrafo}px; line-height: 1.65; color: #374151; text-align: justify; overflow-wrap: break-word; word-break: break-word;`;

const ESTILO_PARRAFO_PLACEHOLDER =
  `margin: 0 0 10px; font-size: ${TAMANOS_FUENTE_CONTRATO.parrafo}px; line-height: 1.65; color: #9ca3af; font-style: italic; text-align: justify; overflow-wrap: break-word; word-break: break-word;`;

const ESTILOS_DOCUMENTO_CONTRATO = `
  .contrato-documento-preview {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow-wrap: break-word;
    word-break: break-word;
  }
  .contrato-documento-preview * {
    box-sizing: border-box;
    max-width: 100%;
  }
`;

/** Marca de agua del logo centrada en toda el área del documento. */
export function construirMarcaDeAguaLogoHtml(logoSrc, { opacidad = 0.1, anchoPorcentaje = 58 } = {}) {
  if (!logoSrc) return '';

  return `
    <div
      aria-hidden="true"
      style="position: absolute; inset: 0; z-index: 0; pointer-events: none; overflow: hidden;"
    >
      <img
        src="${logoSrc}"
        alt=""
        style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: ${anchoPorcentaje}%; max-height: 72%; object-fit: contain; opacity: ${opacidad};"
      />
    </div>
  `;
}

export function construirEncabezadoEmpresaContrato({
  nombreEmpresa,
  ruc,
  direccion,
  borderColor = '#4f46e5',
  titleColor = '#4338ca',
}) {
  return `
    <div style="border-bottom: 2px solid ${borderColor}; padding-bottom: 14px; margin-bottom: 18px; text-align: center;">
      <h1 style="margin: 0 0 6px; font-size: ${TAMANOS_FUENTE_CONTRATO.empresaTitulo}px; font-weight: bold; color: ${titleColor};">${escapeHtml(nombreEmpresa)}</h1>
      <p style="margin: 0; font-size: ${TAMANOS_FUENTE_CONTRATO.empresaDetalle}px; color: #4b5563;">RUC: ${escapeHtml(ruc)}</p>
      ${direccion ? `<p style="margin: 4px 0 0; font-size: ${TAMANOS_FUENTE_CONTRATO.empresaDetalle}px; color: #6b7280;">${escapeHtml(direccion)}</p>` : ''}
    </div>`;
}

/**
 * Contenido HTML del contrato (sin envoltorio de página ni marca de agua).
 */
export function construirContenidoVistaPreviaPlantilla({
  tipo,
  titulo,
  introduccion,
  articulos,
  empresa,
  fuente,
}) {
  const mapa = construirMapaVistaPrevia(empresa);
  const fontFamily = resolverCssFontFamilyContrato(fuente);
  const tituloResuelto = resolverVariablesPlantilla(titulo?.trim() || 'Contrato de trabajo', mapa);
  const tipoTexto = tipo?.trim() || 'Tipo de contrato';
  const nombreEmpresa = mapa['empresa.nombre_empresa'];
  const ruc = mapa['empresa.ruc'];
  const direccion = mapa['empresa.direccion'];
  const representante = mapa['empresa.representante_legal'];
  const dniRep = mapa['empresa.dni_representante'];

  const introTexto = (introduccion || '').trim();
  const bloqueIntroduccion = introTexto
    ? textoContratoParrafosHtml(introTexto, mapa, ESTILO_PARRAFO)
    : `<p data-contrato-parrafo="1" style="${ESTILO_PARRAFO_PLACEHOLDER}">Redacta la introducción (comparecencia de las partes) antes del artículo 1…</p>`;

  const articulosFiltrados = (articulos || []).filter(
    (item) =>
      (item?.titulo || '').trim() ||
      obtenerParrafosArticulo(item).some((p) => String(p).trim()) ||
      (item?.contenido || '').trim()
  );

  const bloquesArticulos = articulosFiltrados
    .map((articulo) => {
      const tituloRaw = tituloArticuloVisible(articulo);
      const tituloHtml = tituloRaw
        ? `<h3 data-contrato-titulo-articulo="1" data-contrato-parrafo="1" style="margin: 16px 0 8px; font-size: ${TAMANOS_FUENTE_CONTRATO.tituloArticulo}px; font-weight: bold; color: #111827;">${textoContratoAHtml(resolverVariablesPlantilla(tituloRaw, mapa))}</h3>`
        : '';

      const parrafos = obtenerParrafosArticulo(articulo).filter((p) => String(p).trim());
      const parrafosHtml = parrafos
        .map(
          (parrafo) =>
            `<p data-contrato-parrafo="1" style="${ESTILO_PARRAFO}">${textoContratoAHtml(
              resolverVariablesPlantilla(parrafo, mapa)
            )}</p>`
        )
        .join('');

      if (!tituloHtml && !parrafosHtml) return '';

      return `
        <div data-contrato-articulo="1" style="margin-bottom: 12px;">
          ${tituloHtml}
          ${parrafosHtml}
        </div>`;
    })
    .join('');

  const pieFecha = `${mapa['contrato.lugar_firma'] ? `${escapeHtml(mapa['contrato.lugar_firma'])}, ` : ''}${escapeHtml(mapa['contrato.fecha_firma'])}`;

  return `
    <div class="contrato-documento-preview" style="color: #111827; font-family: ${fontFamily}; width: 100%; max-width: 100%; box-sizing: border-box;">
      <style>
        ${ESTILOS_DOCUMENTO_CONTRATO}
        .contrato-documento-preview,
        .contrato-documento-preview * {
          font-family: ${fontFamily} !important;
        }
      </style>
      <div>
      ${construirEncabezadoEmpresaContrato({
        nombreEmpresa,
        ruc,
        direccion,
      })}
      </div>

      <div style="margin-bottom: 18px;">
        <p data-contrato-parrafo="1" style="margin: 0 0 4px; font-size: ${TAMANOS_FUENTE_CONTRATO.tipoContrato}px; text-align: center; color: #4f46e5; font-weight: 600;">${escapeHtml(tipoTexto)}</p>
        <h2 data-contrato-parrafo="1" style="margin: 0; font-size: ${TAMANOS_FUENTE_CONTRATO.tituloDocumento}px; font-weight: bold; text-align: center; text-transform: uppercase; color: #111827;">${escapeHtml(tituloResuelto)}</h2>
      </div>

      ${bloqueIntroduccion}

      ${bloquesArticulos || `<p data-contrato-parrafo="1" style="font-size: ${TAMANOS_FUENTE_CONTRATO.parrafo}px; color: #9ca3af; font-style: italic; margin: 0;">Agrega artículos numerados a continuación de la introducción…</p>`}

      <p data-contrato-parrafo="1" style="margin: 22px 0 14px; font-size: ${TAMANOS_FUENTE_CONTRATO.pieFecha}px; text-align: center; color: #4b5563;">${pieFecha}</p>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 14px;">
        <div style="text-align: center; padding: 12px 0;">
          <div style="height: 52px; margin-bottom: 8px; display: flex; align-items: center; justify-content: center; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaHint}px; color: #9ca3af;">Firma del empleador</div>
          <div style="border-top: 1px solid #374151; width: 80%; margin: 0 auto 8px;"></div>
          <p style="margin: 0; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaRol}px; font-weight: bold; color: #374151;">EL EMPLEADOR</p>
          <p style="margin: 6px 0 0; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaNombre}px; color: #111827;">${escapeHtml(representante)}</p>
          ${dniRep ? `<p style="margin: 4px 0 0; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaDetalle}px; color: #6b7280;">DNI: ${escapeHtml(dniRep)}</p>` : ''}
        </div>
        <div style="text-align: center; padding: 12px 0;">
          <div style="height: 52px; margin-bottom: 8px;"></div>
          <div style="border-top: 1px solid #374151; width: 80%; margin: 0 auto 8px;"></div>
          <p style="margin: 0; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaRol}px; font-weight: bold; color: #374151;">EL TRABAJADOR</p>
          <p style="margin: 6px 0 0; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaNombre}px; color: #111827;">${escapeHtml(mapa['empleado.nombre_completo'])}</p>
          <p style="margin: 4px 0 0; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaDetalle}px; color: #6b7280;">${escapeHtml(mapa['empleado.tipo_documento'])}: ${escapeHtml(mapa['empleado.numero_documento'])}</p>
        </div>
      </div>
    </div>
  `;
}

/**
 * Mismo diseño que construirContenidoVistaPreviaPlantilla, con texto ya resuelto (asignación / contrato guardado).
 */
export function construirContenidoVistaPreviaContratoResuelto({
  tipo,
  titulo,
  introduccion,
  articulos,
  mapa = {},
  fuente,
  firmaEmpleadorSrc = null,
}) {
  const fontFamily = resolverCssFontFamilyContrato(fuente);
  const tituloResuelto = (titulo || '').trim() || 'Contrato de trabajo';
  const tipoTexto = (tipo || '').trim() || 'Tipo de contrato';
  const nombreEmpresa = mapa['empresa.nombre_empresa'] || '';
  const ruc = mapa['empresa.ruc'] || '';
  const direccion = mapa['empresa.direccion'] || '';
  const representante = mapa['empresa.representante_legal'] || '';
  const dniRep = mapa['empresa.dni_representante'] || '';

  const introTexto = (introduccion || '').trim();
  const bloqueIntroduccion = introTexto
    ? textoContratoParrafosHtml(introTexto, mapa, ESTILO_PARRAFO)
    : '';

  const articulosFiltrados = (articulos || []).filter(
    (item) =>
      (item?.titulo || '').trim() ||
      obtenerParrafosArticulo(item).some((p) => String(p).trim()) ||
      (item?.contenido || '').trim()
  );

  const bloquesArticulos = articulosFiltrados
    .map((articulo) => {
      const tituloRaw = tituloArticuloVisible(articulo);
      const tituloHtml = tituloRaw
        ? `<h3 data-contrato-titulo-articulo="1" data-contrato-parrafo="1" style="margin: 16px 0 8px; font-size: ${TAMANOS_FUENTE_CONTRATO.tituloArticulo}px; font-weight: bold; color: #111827;">${textoContratoAHtml(tituloRaw)}</h3>`
        : '';

      const parrafos = obtenerParrafosArticulo(articulo).filter((p) => String(p).trim());
      const parrafosHtml = parrafos
        .map(
          (parrafo) =>
            `<p data-contrato-parrafo="1" style="${ESTILO_PARRAFO}">${textoContratoAHtml(parrafo)}</p>`
        )
        .join('');

      if (!tituloHtml && !parrafosHtml) return '';

      return `
        <div data-contrato-articulo="1" style="margin-bottom: 12px;">
          ${tituloHtml}
          ${parrafosHtml}
        </div>`;
    })
    .join('');

  const pieFecha = `${mapa['contrato.lugar_firma'] ? `${escapeHtml(mapa['contrato.lugar_firma'])}, ` : ''}${escapeHtml(mapa['contrato.fecha_firma'] || '')}`;

  return `
    <div class="contrato-documento-preview" style="color: #111827; font-family: ${fontFamily}; width: 100%; max-width: 100%; box-sizing: border-box;">
      <style>
        ${ESTILOS_DOCUMENTO_CONTRATO}
        .contrato-documento-preview,
        .contrato-documento-preview * {
          font-family: ${fontFamily} !important;
        }
      </style>
      <div>
      ${construirEncabezadoEmpresaContrato({
        nombreEmpresa,
        ruc,
        direccion,
      })}
      </div>

      <div style="margin-bottom: 18px;">
        <p data-contrato-parrafo="1" style="margin: 0 0 4px; font-size: ${TAMANOS_FUENTE_CONTRATO.tipoContrato}px; text-align: center; color: #4f46e5; font-weight: 600;">${escapeHtml(tipoTexto)}</p>
        <h2 data-contrato-parrafo="1" style="margin: 0; font-size: ${TAMANOS_FUENTE_CONTRATO.tituloDocumento}px; font-weight: bold; text-align: center; text-transform: uppercase; color: #111827;">${escapeHtml(tituloResuelto)}</h2>
      </div>

      ${bloqueIntroduccion}

      ${bloquesArticulos}

      <p data-contrato-parrafo="1" style="margin: 22px 0 14px; font-size: ${TAMANOS_FUENTE_CONTRATO.pieFecha}px; text-align: center; color: #4b5563;">${pieFecha}</p>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 14px;">
        <div style="text-align: center; padding: 12px 0;">
          <div style="height: 52px; margin-bottom: 8px; display: flex; align-items: center; justify-content: center; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaHint}px; color: #9ca3af;">
            ${firmaEmpleadorSrc
              ? `<img src="${firmaEmpleadorSrc}" alt="" style="max-width: 160px; max-height: 52px; object-fit: contain;" />`
              : 'Firma del empleador'}
          </div>
          <div style="border-top: 1px solid #374151; width: 80%; margin: 0 auto 8px;"></div>
          <p style="margin: 0; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaRol}px; font-weight: bold; color: #374151;">EL EMPLEADOR</p>
          <p style="margin: 6px 0 0; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaNombre}px; color: #111827;">${escapeHtml(representante)}</p>
          ${dniRep ? `<p style="margin: 4px 0 0; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaDetalle}px; color: #6b7280;">DNI: ${escapeHtml(dniRep)}</p>` : ''}
        </div>
        <div style="text-align: center; padding: 12px 0;">
          <div style="height: 52px; margin-bottom: 8px;"></div>
          <div style="border-top: 1px solid #374151; width: 80%; margin: 0 auto 8px;"></div>
          <p style="margin: 0; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaRol}px; font-weight: bold; color: #374151;">EL TRABAJADOR</p>
          <p style="margin: 6px 0 0; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaNombre}px; color: #111827;">${escapeHtml(mapa['empleado.nombre_completo'] || '')}</p>
          <p style="margin: 4px 0 0; font-size: ${TAMANOS_FUENTE_CONTRATO.firmaDetalle}px; color: #6b7280;">${escapeHtml(mapa['empleado.tipo_documento'] || '')}: ${escapeHtml(mapa['empleado.numero_documento'] || '')}</p>
        </div>
      </div>
    </div>
  `;
}

/**
 * HTML de vista previa del contrato mientras se edita la plantilla.
 * @deprecated Preferir construirContenidoVistaPreviaPlantilla + ContratoVistaPreviaPaginada
 */
export function construirHtmlVistaPreviaPlantilla({ tipo, titulo, introduccion, articulos, empresa, fuente, logoSrc }) {
  const contenido = construirContenidoVistaPreviaPlantilla({
    tipo,
    titulo,
    introduccion,
    articulos,
    empresa,
    fuente,
  });

  return `
    <div style="position: relative; min-height: 297mm;">
      ${construirMarcaDeAguaLogoHtml(logoSrc)}
      <div style="position: relative; z-index: 1;">
        ${contenido}
        <p style="margin: 14px 0 0; font-size: 10px; color: #9ca3af; text-align: center;">
          Vista previa con datos de ejemplo. Al generar el contrato se usarán los datos reales del empleado.
        </p>
      </div>
    </div>
  `;
}
